import { getBearerToken } from './parts/getBearerToken';
import { getTranslatedQuery } from './parts/getTranslatedQuery';
import { downloadData, GATEWAY_TIMEOUT_STATUS } from './parts/downloadData';
import { DownloadRequestError } from './parts/DownloadRequestError';
import downloadFile from '../../utils/downloadFile';
import { DownloadButtonProps } from '@/types/download/DownloadButtonProps';
import { useToast } from '@/storybook/components/Toast/Toast';
import { useTranslation } from 'react-i18next';
import { CubejsApi } from '@cubejs-client/core';
import { CubeQuery } from '@/types/cube';

const isGatewayTimeoutError = (error: unknown): boolean =>
  error instanceof DownloadRequestError && error.status === GATEWAY_TIMEOUT_STATUS;

export const useDownloadDataButton = ({
  cubejsApi,
  baseQuery,
  queryFilters,
  dateRange,
  translationsMap,
  fileNamePrefix,
  additionalQueryParser,
  mergeValues,
}: Omit<DownloadButtonProps, 'disabled'>) => {
  const { makeToast } = useToast();
  const { t } = useTranslation();
  return async () => {
    try {
      const bearerToken = await getBearerToken();
      const translatedQuery = await getTranslatedQuery({
        queryFilters,
        cubejsApi,
        baseQuery,
        translationsMap,
        dateRange,
        fileNamePrefix,
        additionalQueryParser,
      });
      const fileUrl = await downloadData({ bearerToken, fileNamePrefix, translatedQuery, mergeValues });
      if (fileUrl) {
        await downloadFile(fileUrl);
        makeToast({ content: t('main.download.success'), appearance: 'success' });
      } else {
        makeToast({ content: t('main.download.failure'), appearance: 'error' });
      }
    } catch (error) {
      makeToast({
        content: isGatewayTimeoutError(error) ? t('main.download.tooLarge') : t('main.download.error'),
        appearance: 'error',
      });
      throw error;
    }
  };
};

const mockCubejsApi = {} as CubejsApi;

const mockBaseQuery: CubeQuery = {
  measures: [],
  dimensions: ['Users.city'],
  limit: 500,
  filters: [],
  timeDimensions: [],
  order: {},
};

export const MOCK_DOWNLOAD_BUTTON_PROPS: DownloadButtonProps = {
  baseQuery: mockBaseQuery,
  cubejsApi: mockCubejsApi,
  queryFilters: { and: [] },
  dateRange: { dimension: 'test', dateRange: 'last 30 days' },
  translationsMap: {},
  fileNamePrefix: 'test',
};
